import { ChevronDown } from "baseui/icon";
import classNames from "classnames";
import moment from "moment";
import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { LuCalendar, LuChevronDown } from "react-icons/lu";

import { StyledDatePicker } from "../../components/datepicker/datepicker";
import { TimePeriod } from "../../hooks/useDetectTimePeriod";

export type Period = {
  period: TimePeriod | null;
  from: Date | null;
  to: Date | null;
};

type Props = {
  value: Period | null;
  setValue: (value: Period) => void;
  small?: boolean;
  /** Billing pages (V49 design). */
  billing?: boolean;
  hasAll?: boolean;
  autoApplyCustomRange?: boolean;
  showCalendarIcon?: boolean;
  customLabelPrefix?: string;
  displayFormat?: string;
  datePickerFormat?: string;
  showPlaceholderWhenEmpty?: boolean;
};

export const SelectDate: FC<Props> = ({
  value,
  setValue,
  small = false,
  billing = false,
  hasAll = true,
  autoApplyCustomRange = false,
  showCalendarIcon = false,
  customLabelPrefix = "Custom",
  displayFormat = "MMM DD, YYYY",
  datePickerFormat = "MMM dd, yyyy",
  showPlaceholderWhenEmpty = false,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuIdRef = useRef(
    `select-date-dropdown-${Math.random().toString(36).slice(2)}`,
  );
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null);

  const defaultPeriod = hasAll ? TimePeriod.All : TimePeriod.LastYear;

  const data = useMemo(() => {
    const options = [
      { value: TimePeriod.Today, label: "Today" },
      { value: TimePeriod.ThisMonth, label: "This month" },
      { value: TimePeriod.LastMonth, label: "Last month" },
      { value: TimePeriod.YTD, label: "YTD" },
      { value: TimePeriod.LastYear, label: "Last 12 months" },
      { value: TimePeriod.Custom, label: "Custom" },
    ];

    if (!hasAll) {
      return options;
    }

    return [{ value: TimePeriod.All, label: "All" }, ...options];
  }, [hasAll]);

  const getSafePeriod = useCallback(
    (period: TimePeriod | null | undefined) => {
      if (showPlaceholderWhenEmpty && !period) {
        return null;
      }

      if (!hasAll && period === TimePeriod.All) {
        return TimePeriod.LastYear;
      }

      return period ?? defaultPeriod;
    },
    [defaultPeriod, hasAll, showPlaceholderWhenEmpty],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [activeValue, setActiveValue] = useState<TimePeriod | null>(
    getSafePeriod(value?.period),
  );
  const [fromDate, setFromDate] = useState<Date | null>(value?.from || null);
  const [toDate, setToDate] = useState<Date | null>(value?.to || null);

  const onClose = useCallback(() => {
    if (activeValue !== TimePeriod.Custom) {
      setFromDate(null);
      setToDate(null);
      setIsOpen(false);
      return;
    }

    if (autoApplyCustomRange) {
      setActiveValue(getSafePeriod(value?.period));
      setFromDate(value?.from || null);
      setToDate(value?.to || null);
      setIsOpen(false);
    }
  }, [activeValue, autoApplyCustomRange, getSafePeriod, value]);

  const updateMenuPosition = useCallback(() => {
    const node = dropdownRef.current;

    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();

    setMenuStyle({
      left: rect.left,
      position: "fixed",
      top: rect.bottom + 8,
      width: rect.width,
      zIndex: 9999,
    });
  }, []);

  const handleSelect = (period: TimePeriod) => {
    if (period === TimePeriod.Custom) {
      setActiveValue(period);
      return;
    }

    setActiveValue(period);
    setValue({ period, from: null, to: null });
    setFromDate(null);
    setToDate(null);
    setIsOpen(false);
  };

  const onApply = () => {
    setIsOpen(false);

    setValue({
      period: activeValue ?? defaultPeriod,
      from: fromDate,
      to: toDate,
    });
  };

  const onCancel = () => {
    setIsOpen(false);
    setActiveValue(getSafePeriod(value?.period));
    setFromDate(value?.from || null);
    setToDate(value?.to || null);
  };

  const setCustomRange = (
    nextFromDate: Date | null,
    nextToDate: Date | null,
  ) => {
    setFromDate(nextFromDate);
    setToDate(nextToDate);

    if (!autoApplyCustomRange) {
      return;
    }

    setValue({
      period: TimePeriod.Custom,
      from: nextFromDate,
      to: nextToDate,
    });
  };

  const selectedLabel = useMemo(() => {
    if (activeValue === TimePeriod.Custom && (fromDate || toDate)) {
      const fromLabel = moment(fromDate || new Date()).format(displayFormat);
      const toLabel = moment(toDate || new Date()).format(displayFormat);

      return `${customLabelPrefix} - ${fromLabel} - ${toLabel}`;
    }

    return (
      data.find((item) => item.value === activeValue)?.label || "Select..."
    );
  }, [activeValue, customLabelPrefix, data, displayFormat, fromDate, toDate]);

  useEffect(() => {
    setActiveValue(getSafePeriod(value?.period));
    setFromDate(value?.from || null);
    setToDate(value?.to || null);
  }, [getSafePeriod, hasAll, value]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return;
    }

    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (
        target instanceof Element &&
        target.closest(
          "[data-baseweb='calendar'], [data-baseweb='popover'], [data-baseweb='datepicker']",
        )
      ) {
        return;
      }

      if (
        dropdownRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }

      onClose();
    };

    const handleWindowChange = () => {
      updateMenuPosition();
    };

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [isOpen, onClose, updateMenuPosition]);

  const menu = (
    <div
      id={menuIdRef.current}
      ref={menuRef}
      style={menuStyle || undefined}
      className={classNames(
        "rounded-[8px] border border-gray-300 bg-white py-1 shadow-lg",
        small ? "text-sm" : "",
      )}>
      {data.map((item) => (
        <div
          key={item.value}
          onClick={() => handleSelect(item.value)}
          className={classNames(
            "cursor-pointer font-semibold hover:bg-gray-100",
            small ? "px-3 py-1.5 text-xs" : "px-3 py-2 text-sm",
          )}>
          {item.label}
        </div>
      ))}

      {activeValue === TimePeriod.Custom && (
        <div className={classNames("px-3", small ? "pt-1" : "")}>
          <div
            className={classNames(
              "font-medium",
              small ? "mb-1 text-xs" : "mb-1 text-sm",
            )}>
            From
          </div>

          <div>
            <StyledDatePicker
              maxDate={toDate || moment().endOf("D").toDate()}
              value={fromDate || moment().startOf("D").toDate()}
              onChange={(date) => {
                const nextFromDate = moment(date).startOf("D").toDate();
                const nextToDate = toDate || moment().endOf("D").toDate();

                setCustomRange(nextFromDate, nextToDate);
              }}
              mountNode={menuIdRef.current}
              format={datePickerFormat}
              size={"sm"}
              popoverZIndex={10001}
              showCalendarIcon
            />
          </div>

          <div
            className={classNames(
              "font-medium",
              small ? "mb-1 mt-2 text-xs" : "mb-1 mt-3 text-sm",
            )}>
            To
          </div>

          <div>
            <StyledDatePicker
              minDate={fromDate || moment().startOf("D").toDate()}
              value={toDate || moment().endOf("D").toDate()}
              onChange={(date) => {
                const nextFromDate = fromDate || moment().startOf("D").toDate();
                const nextToDate = moment(date).endOf("D").toDate();

                setCustomRange(nextFromDate, nextToDate);
              }}
              mountNode={menuIdRef.current}
              format={datePickerFormat}
              size={"sm"}
              popoverZIndex={10001}
              showCalendarIcon
            />
          </div>

          {!autoApplyCustomRange && (
            <div
              className={classNames(
                "grid grid-cols-2 gap-2",
                small ? "mt-2" : "mt-3",
              )}>
              <button
                type={"button"}
                onClick={onApply}
                className={classNames(
                  "bg-purple w-full rounded-md border-none normal-case text-white",
                  small ? "h-8 text-xs" : "btn",
                )}>
                Apply
              </button>

              <button
                type={"button"}
                onClick={onCancel}
                className={classNames(
                  "w-full rounded-md border border-gray-300 bg-white normal-case",
                  small ? "h-8 text-xs" : "btn btn-outline",
                )}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (billing) {
    return (
      <div ref={dropdownRef} className={"h-[38px] w-full"}>
        <div
          className={
            "flex h-full w-full cursor-pointer items-center justify-between gap-2 rounded-[9px] border border-[#DFE2E8] bg-white px-3 hover:border-[#6B4EFF]"
          }
          onClick={() => setIsOpen(true)}>
          <div
            className={classNames(
              "truncate text-[13px]",
              activeValue
                ? "font-semibold text-[#3E64D9]"
                : "font-normal text-[#9AA0B0]",
            )}>
            {selectedLabel}
          </div>
          {showCalendarIcon ? (
            <LuCalendar aria-hidden className={"text-[#9AA0B0]"} size={15} />
          ) : (
            <LuChevronDown aria-hidden className={"text-[#9AA0B0]"} size={14} />
          )}
        </div>
        {isOpen && menuStyle ? createPortal(menu, document.body) : null}
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className={classNames("w-full", small ? "h-8" : "h-12")}>
      <div
        className={classNames(
          "flex h-full w-full cursor-pointer items-center justify-between rounded-[8px] border border-gray-300",
          small ? "py-1" : "py-2",
        )}
        onClick={() => setIsOpen(true)}>
        <div
          className={classNames(
            "truncate font-medium",
            small ? "px-2 text-sm" : "px-2 text-base",
            !activeValue && "text-gray-500",
          )}>
          {selectedLabel}
        </div>

        <div
          className={classNames(
            "flex shrink-0 items-center justify-center border-l border-gray-300",
            small ? "h-6 w-8" : "h-[30px] w-9",
          )}>
          {showCalendarIcon ? (
            <LuCalendar
              aria-hidden
              className={"text-[#7A8694]"}
              size={small ? 16 : 18}
            />
          ) : (
            <ChevronDown color={"rgba(0,0,0,0.2)"} size={small ? 22 : 28} />
          )}
        </div>
      </div>
      {isOpen && menuStyle ? createPortal(menu, document.body) : null}
    </div>
  );
};
